/**
 * Feb 2026 · Inline "Latest comment" cell for the Tasks list.
 *
 * Shows the latest comment body (or "Add comment…" placeholder) and
 * opens a tiny popover when clicked. The popover carries a textarea
 * and an Internal/Client toggle that mirrors the backend's
 * `visibility` enum. Submitting POSTs to
 * `/api/v1/tasks/{tid}/comments`, which denormalises
 * `latest_comment_*` back onto the task — the caller is responsible
 * for re-fetching (`onPosted` is called with the saved doc).
 *
 * Rendered via `buildTaskColumns` so it appears on both the Tasks
 * page and the Projects → Tasks sub-tab with identical behaviour.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

export default function TaskCommentCell({ task, canEdit, onPosted, fmtRelative }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [saving, setSaving] = useState(false);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  // Feb 2026 · @mention state. We fetch workspace members once per
  // open (cheap endpoint, same list the assignee picker already
  // hits) and show a floating list filtered by the typed @prefix.
  const [members, setMembers] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIdx, setMentionIdx] = useState(0);
  const [mentionedIds, setMentionedIds] = useState([]);
  const taRef = useRef(null);

  const loadThread = async () => {
    setThreadLoading(true);
    try {
      const { data } = await api.get(`/tasks/${task.id}/comments`);
      setThread(Array.isArray(data) ? data : []);
    } catch (e) {
      setThread([]);
      toast.error(apiErrorMessage(e, "Failed to load thread"));
    } finally {
      setThreadLoading(false);
    }
  };

  const loadMembers = async () => {
    if (members.length) return;
    try {
      const { data } = await api.get(
        `/workspaces/${task.workspace_id}/members`);
      setMembers(Array.isArray(data) ? data : []);
    } catch { /* silent — fall back to no autocomplete */ }
  };

  useEffect(() => {
    if (open) {
      setBody("");
      setVisibility("internal");
      setThread(null);
      setMentionedIds([]);
      setMentionOpen(false);
      loadThread();
      loadMembers();
      setTimeout(() => taRef.current?.focus(), 40);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task.id]);

  /** Filtered mention candidates for the current @query. Case-insensitive
      match against name OR email, caps at 8 rows. */
  const mentionMatches = React.useMemo(() => {
    const q = (mentionQuery || "").toLowerCase().trim();
    const all = (members || [])
      .filter((m) => (m.user_id || m.id) && (m.name || m.email))
      .map((m) => ({
        id: m.user_id || m.id,
        name: m.name || m.email || "Teammate",
        email: m.email || "",
      }));
    if (!q) return all.slice(0, 8);
    return all.filter((m) =>
      m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [members, mentionQuery]);

  /** Scan the text to the left of the caret for an `@word` token that
      isn't preceded by a non-space character — only trigger when @ is
      typed as a mention, not inside an email. */
  const detectMention = () => {
    const el = taRef.current;
    if (!el) return;
    const caret = el.selectionStart;
    const before = (el.value || "").slice(0, caret);
    const m = before.match(/(^|\s)@([a-zA-Z0-9._-]{0,30})$/);
    if (m) {
      setMentionQuery(m[2]);
      setMentionOpen(true);
      setMentionIdx(0);
    } else {
      setMentionOpen(false);
    }
  };

  const insertMention = (member) => {
    const el = taRef.current;
    if (!el) return;
    const caret = el.selectionStart;
    const before = (el.value || "").slice(0, caret);
    const after = (el.value || "").slice(caret);
    const replaced = before.replace(/(^|\s)@([a-zA-Z0-9._-]{0,30})$/,
      (_m, pre) => `${pre}@${member.name} `);
    const next = replaced + after;
    setBody(next);
    setMentionedIds((prev) => prev.includes(member.id) ? prev : [...prev, member.id]);
    setMentionOpen(false);
    // Restore caret just past the inserted mention.
    requestAnimationFrame(() => {
      const pos = replaced.length;
      el.focus();
      try { el.setSelectionRange(pos, pos); } catch { /* ignore */ }
    });
  };

  const onKeyDown = (e) => {
    if (mentionOpen && mentionMatches.length) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIdx((i) => (i + 1) % mentionMatches.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIdx((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(mentionMatches[mentionIdx]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionOpen(false);
        return;
      }
    }
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setSaving(true);
    try {
      // Filter out mentionedIds whose name string isn't still in the
      // body text — a user may have typed @ then deleted the name.
      const effectiveMentions = mentionedIds.filter((uid) => {
        const m = members.find((mb) => (mb.user_id || mb.id) === uid);
        return m && text.includes(`@${m.name || m.email || ""}`);
      });
      const { data } = await api.post(`/tasks/${task.id}/comments`,
        { body: text, visibility,
           mentioned_user_ids: effectiveMentions.length ? effectiveMentions : undefined });
      toast.success(
        effectiveMentions.length
          ? `Comment posted · ${effectiveMentions.length} mention${effectiveMentions.length === 1 ? "" : "s"} notified`
          : (visibility === "client" ? "Comment posted (client-visible)" : "Comment posted"));
      setThread((prev) => Array.isArray(prev) ? [...prev, data] : [data]);
      setBody("");
      setMentionedIds([]);
      onPosted?.(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to post comment"));
    } finally {
      setSaving(false);
    }
  };

  const latest = task.latest_comment_body || "";
  const triggerLabel = latest ? (
    <div className="min-w-0 text-left">
      <div className="text-xs truncate italic">&ldquo;{latest}&rdquo;</div>
      <div className="text-[10px] text-muted-foreground truncate">
        {task.latest_comment_author_name || "—"} · {task.latest_comment_at && fmtRelative ? fmtRelative(task.latest_comment_at) : ""}
      </div>
    </div>
  ) : (
    <span className="text-muted-foreground italic text-xs">
      {canEdit ? "Add comment…" : "—"}
    </span>
  );

  return (
    <Popover open={open} onOpenChange={(v) => canEdit && setOpen(v)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={!canEdit}
          onClick={(e) => e.stopPropagation()}
          className={`w-full text-left px-1 py-0.5 ${canEdit ? "cursor-pointer hover:bg-muted/40" : "cursor-default"} rounded-none`}
          title={latest || (canEdit ? "Click to post a comment" : "No comments")}
          data-testid={`task-comment-cell-${task.id}`}
        >
          {triggerLabel}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 p-3 rounded-none"
        align="start"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        data-testid={`task-comment-popover-${task.id}`}
      >
        <div className="space-y-3">
          {/* Thread peek — newest 20 comments + a count hint if the
              thread is longer. Rendered first so the composer sits
              under the context, matching inbox conventions. */}
          <div
            className="border border-border bg-muted/30 max-h-56 overflow-y-auto p-2 space-y-1.5 text-xs"
            data-testid={`task-comment-thread-${task.id}`}
          >
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center justify-between">
              <span>Thread</span>
              {Array.isArray(thread) && thread.length > 0 && (
                <span className="text-[10px] font-normal">
                  {thread.length > 20 ? `last 20 of ${thread.length}` : `${thread.length} comment${thread.length === 1 ? "" : "s"}`}
                </span>
              )}
            </div>
            {threadLoading && (!thread || thread.length === 0) ? (
              <div className="text-[11px] text-muted-foreground italic">Loading…</div>
            ) : !thread || thread.length === 0 ? (
              <div className="text-[11px] text-muted-foreground italic">No comments yet.</div>
            ) : (
              thread.slice(-20).map((c, i) => {
                const isSystem = c.kind === "system" || c.visibility === "system";
                const label = c.author_name || c.user_name || c.actor_name || c.email || "Unknown";
                const when = (c.created_at || "").slice(0, 16).replace("T", " ");
                const body = c.body || c.body_text || c.text || "";
                const vis = c.visibility;
                return (
                  <div
                    key={c.id || i}
                    className={`py-1 ${i > 0 ? "border-t border-border/60" : ""}`}
                    data-testid={`task-comment-thread-row-${task.id}-${i}`}
                  >
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`font-semibold text-[11px] ${isSystem ? "text-slate-500" : "text-foreground"}`}>
                        {isSystem ? "System" : label}
                      </span>
                      {vis === "client" && (
                        <span className="chip text-[9px] bg-amber-50 border-amber-400 text-amber-700 px-1 py-0 font-semibold">CLIENT</span>
                      )}
                      {vis === "internal" && !isSystem && (
                        <span className="chip text-[9px] bg-slate-100 border-slate-300 text-slate-700 px-1 py-0">INT</span>
                      )}
                      <span className="text-[10px] text-muted-foreground ml-auto tabular-nums">{when}</span>
                    </div>
                    <div className={`whitespace-pre-wrap break-words ${isSystem ? "text-[11px] text-muted-foreground italic" : "text-xs text-foreground"}`}>
                      {body}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Reply
          </div>
          <div className="relative">
            <textarea
              ref={taRef}
              value={body}
              onChange={(e) => { setBody(e.target.value); detectMention(); }}
              onKeyUp={detectMention}
              onClick={detectMention}
              placeholder="Type a comment… @ to mention a teammate"
              rows={3}
              className="w-full text-xs px-2 py-1.5 border border-border bg-background rounded-none focus:outline-none focus:border-primary resize-none"
              onKeyDown={onKeyDown}
              data-testid={`task-comment-input-${task.id}`}
            />
            {mentionOpen && mentionMatches.length > 0 && (
              <div
                className="absolute left-0 right-0 bottom-full mb-1 border border-border bg-background shadow-lg z-[60] max-h-48 overflow-y-auto"
                data-testid={`task-comment-mention-list-${task.id}`}
                onMouseDown={(e) => e.preventDefault()}
              >
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold px-2 pt-1.5 pb-1">
                  Mention teammate · ↑↓ Enter
                </div>
                {mentionMatches.map((m, i) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => insertMention(m)}
                    className={`w-full text-left px-2 py-1.5 text-xs border-t border-border/60 hover:bg-muted/70 ${i === mentionIdx ? "bg-primary/10" : ""}`}
                    data-testid={`task-comment-mention-${task.id}-${m.id}`}
                  >
                    <div className="font-semibold truncate">{m.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{m.email}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Visibility toggle — mirrors the thread's `visibility`
                field. Internal = default; Client = visible to the
                customer portal. */}
            <div
              className="inline-flex border border-border text-[11px]"
              role="radiogroup"
              aria-label="Comment visibility"
            >
              {["internal", "client"].map((v) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={visibility === v}
                  onClick={() => setVisibility(v)}
                  className={`px-2 py-0.5 ${visibility === v
                    ? (v === "client"
                      ? "bg-amber-500 text-white font-semibold"
                      : "bg-slate-800 text-white font-semibold")
                    : "bg-background text-muted-foreground hover:bg-muted/40"}`}
                  data-testid={`task-comment-vis-${v}-${task.id}`}
                >
                  {v === "internal" ? "Internal" : "Client"}
                </button>
              ))}
            </div>
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[11px] px-2 py-0.5 text-muted-foreground hover:text-foreground"
              data-testid={`task-comment-cancel-${task.id}`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!body.trim() || saving}
              className="text-[11px] px-2.5 py-0.5 bg-primary text-primary-foreground font-semibold disabled:opacity-50 hover:bg-primary/90"
              data-testid={`task-comment-submit-${task.id}`}
            >
              {saving ? "Posting…" : "Post"}
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            {visibility === "client"
              ? "Visible in the client portal. Use for status updates and client-facing notes."
              : "Only teammates can see this comment."}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
